import { nivelDiferencia } from './caja';
// src/utils/corteReport.ts
// ============================================================================
// Textos de WhatsApp de la CAJA (apertura y corte). Port del motor de la agenda
// dental (agenda/src/utils/corteReport.ts) adaptado al modelo letsmove:
// métodos cash/wire/creditcard, sin vitrina ni dólares, y SIN sistema de
// plantillas (v1 usa los defaults compilados; el motor @vars queda listo por si
// algún día se editan). El envío es click-to-chat (wa.me) — letsmove no usa WAHA.
// ============================================================================

const money = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

const fechaHora = (d: Date) => ({
  fecha: d.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' }),
  hora: d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }),
});

export const CAJA_APERTURA_TEMPLATE_DEFAULT =
  '🟢 *Caja abierta — @negocio*\n' +
  '📅 @fecha · ⏰ @hora\n' +
  '🔆 Turno: @turno\n' +
  '💵 Fondo inicial: @fondo\n' +
  '👤 Abrió: @usuario';

export const CAJA_CORTE_TEMPLATE_DEFAULT =
  '🧾 *CORTE DE CAJA — @negocio*\n' +
  '📅 @fecha · ⏰ @hora\n' +
  '🔆 Turno: @turno@cerradapor\n' +
  '\n' +
  '💰 *Cobros del turno*\n' +
  '@cobros\n' +
  '\n' +
  '💵 *Arqueo de efectivo*\n' +
  '@arqueo@movimientos@conteovitrina@nota';

/** Sustituye variables @x; las más largas primero para que @efectivo no sea
 *  pisado por un hipotético @efe. Desconocidas quedan tal cual. */
export const renderCajaTemplate = (content: string, vars: Record<string, string>): string => {
  let out = content;
  for (const key of Object.keys(vars).sort((a, b) => b.length - a.length)) {
    out = out.split(`@${key}`).join(vars[key]);
  }
  return out;
};

/** Shape del POST /finances/cashbox/close (F1) — lo que necesita el corte. */
export interface CorteData {
  shift_label?: string | null;
  open_amount: number;
  cash_collected: number;
  movements_net: number;
  movements: { kind: 'withdrawal' | 'deposit'; amount: number; reason: string | null }[];
  methods: { cash: number; wire: number; creditcard: number; other: number; num_payments: number; usd?: number };
  vitrina?: { total: number; n: number; units?: number };
  /** Dólares del turno (1-oct-2026): se cuadran aparte de los pesos. null = la caja no manejó dólares. */
  usd?: { expected: number; close: number; difference: number } | null;
  /** Fiado del turno sin pagar. El de ALUMNAS ya viene sumado en `expected_amount` (Carlos 29-sep:
   *  cuenta como ingreso; al cierre debe estar). El de maestros se acumula y no suma. */
  fiado_alumnas?: number; fiado_maestros?: number;
  /** Conteo de vitrina del turno. null/undefined = inventario apagado: el corte no dice nada. */
  vitrina_conteo?: {
    counted: boolean; by?: string | null; lines_counted?: number; lines_diff?: number;
    diffs?: { name: string; expected: number | null; counted: number; diff: number | null }[];
    /** Por surtir tras el conteo (rojo/ámbar), los rojos primero. `buy` = piezas hasta el tope. */
    low?: { name: string; stock: number; level: 'rojo' | 'ambar'; buy: number | null }[];
    /** Cuando este turno NO contó: el último conteo que hubo (null = nunca). */
    last?: { at: string; by: string | null; shift_label: string | null } | null;
  } | null;
  expected_amount: number;
  close_amount: number;
  difference: number;
  notes?: string | null;
  closed_by_name?: string | null;
}

export const buildCorteVars = (c: CorteData, negocio: string): Record<string, string> => {
  const { fecha, hora } = fechaHora(new Date());
  const m = c.methods;

  const dif = c.difference || 0;
  // Mismo criterio que la pantalla (utils/caja.ts): antes aquí «cuadró» era <$0.01 y en el panel
  // ≤$5 se llamaba «diferencia menor», así que un corte podía salir «cuadró» en un sitio y
  // «falta $3» en el otro.
  const cuadrado = nivelDiferencia(dif) === 'cuadra';
  const difLinea = cuadrado
    ? '✅ *Cuadró exacto*'
    : dif < 0 ? `⚠️ *Faltante: ${money(Math.abs(dif))}*` : `🟡 *Sobrante: ${money(dif)}*`;

  const total = (m?.cash || 0) + (m?.creditcard || 0) + (m?.wire || 0) + (m?.other || 0);
  const cobros: string[] = [];
  cobros.push(`💵 Efectivo: ${money(m?.cash || 0)}`);
  // Dólares (1-oct-2026): su equivalente en pesos ya va en «Efectivo»; aquí cuántos USD fueron.
  if ((m?.usd || 0) > 0) cobros.push(`💲 De eso en dólares: ${m!.usd} USD`);
  cobros.push(`💳 Tarjeta: ${money(m?.creditcard || 0)}`);
  cobros.push(`🏦 Transferencia: ${money(m?.wire || 0)}`);
  if ((m?.other || 0) > 0) cobros.push(`• Otros: ${money(m?.other || 0)}`);
  if ((c.vitrina?.total || 0) > 0) {
    const u = c.vitrina!.units || 0;
    cobros.push(`🛍️ Vitrina: ${money(c.vitrina!.total)} · ${u > c.vitrina!.n ? `${u} artículo(s)` : `${c.vitrina!.n} venta(s)`}`);
  }
  cobros.push(`🧮 *Total del turno: ${money(total)}*`);
  cobros.push(`📊 ${m?.num_payments || 0} cobro(s)`);

  const entradas = (c.movements || []).filter((x) => x.kind === 'deposit').reduce((s, x) => s + x.amount, 0);
  const salidas = (c.movements || []).filter((x) => x.kind === 'withdrawal').reduce((s, x) => s + x.amount, 0);
  const arqueo: string[] = [];
  arqueo.push(`Fondo inicial: ${money(c.open_amount || 0)}`);
  arqueo.push(`+ Efectivo cobrado: ${money(c.cash_collected || 0)}`);
  if (entradas > 0) arqueo.push(`+ Entradas: ${money(entradas)}`);
  if (salidas > 0) arqueo.push(`− Salidas: ${money(salidas)}`);
  if ((c.fiado_alumnas || 0) > 0) arqueo.push(`+ Fiado a alumnas (sin cobrar): ${money(c.fiado_alumnas!)}`);
  arqueo.push(`= Esperado: ${money(c.expected_amount || 0)}`);
  if ((c.fiado_maestros || 0) > 0) arqueo.push(`(Maestros deben ${money(c.fiado_maestros!)} de vitrina · no suma al cajón)`);
  arqueo.push(`Contado: ${money(c.close_amount || 0)}`);
  arqueo.push(difLinea);
  // Los dólares se cuadran aparte (1-oct-2026): esperado vs contado en USD.
  if (c.usd) arqueo.push(`💲 Dólares: esperado ${c.usd.expected} USD · contado ${c.usd.close} USD${c.usd.difference === 0 ? ' · cuadran' : ` · ${c.usd.difference > 0 ? 'sobran' : 'faltan'} ${Math.abs(c.usd.difference)} USD`}`);

  let movimientos = '';
  if (c.movements && c.movements.length) {
    movimientos = '\n\n🔁 *Movimientos de efectivo*\n' + c.movements
      .map((x) => `${x.kind === 'withdrawal' ? '−' : '+'}${money(x.amount)} · ${x.reason || (x.kind === 'withdrawal' ? 'Retiro' : 'Depósito')}`)
      .join('\n');
  }

  // CONTEO DE VITRINA (29-sep-2026): lo ve quien no está en el estudio. Turno sin contar = una
  // línea de aviso; contado = las diferencias (tope de 8 renglones para que el mensaje no crezca).
  let conteovitrina = '';
  const cv = c.vitrina_conteo;
  if (cv) {
    if (!cv.counted) {
      // El conteo no es obligatorio (Carlos 29-sep-2026): se informa cuándo fue el último, sin regañar.
      const u = cv.last;
      const cuando = u ? `${new Date(u.at).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}${u.by ? ` · ${u.by}` : ''}${u.shift_label ? ` · ${u.shift_label}` : ''}` : 'nunca';
      conteovitrina = `\n\n🛍️ *Vitrina*\nEste turno no se contó · último conteo: ${cuando}`;
    } else {
      const difs = (cv.diffs || []).filter((d) => d.diff != null && d.diff !== 0);
      const quien = cv.by ? ` · contó ${cv.by}` : '';
      const lineas = difs.slice(0, 8).map((d) => {
        const n = Math.abs(d.diff as number);
        return `• ${d.name}: ${(d.diff as number) < 0 ? `falta${n === 1 ? '' : 'n'}` : `sobra${n === 1 ? '' : 'n'}`} ${n} (esperaba ${d.expected}, hay ${d.counted})`;
      });
      if (difs.length > 8) lineas.push(`• …y ${difs.length - 8} más`);
      conteovitrina = `\n\n🛍️ *Vitrina* · ${cv.lines_counted || 0} producto(s) contado(s)${quien}\n`
        + (difs.length ? lineas.join('\n') : '✅ Todo coincidió');
      // Semáforo (etapa 3): lo que quedó en rojo o ámbar, con cuánto comprar si hay tope.
      const low = cv.low || [];
      if (low.length) {
        const item = (l: NonNullable<typeof cv.low>[number]) => `${l.level === 'rojo' ? '🔴' : '🟡'} ${l.name}: ${l.stock}${l.buy ? ` (comprar ${l.buy})` : ''}`;
        conteovitrina += '\n📦 *Por surtir*\n' + low.slice(0, 8).map(item).join('\n') + (low.length > 8 ? `\n• …y ${low.length - 8} más` : '');
      }
    }
  }

  return {
    negocio,
    fecha,
    hora,
    turno: c.shift_label || 'Único',
    usuario: c.closed_by_name || '—',
    cerradapor: c.closed_by_name ? `\n👤 Cerró: ${c.closed_by_name}` : '',
    fondo: money(c.open_amount || 0),
    efectivo: money(m?.cash || 0),
    tarjeta: money(m?.creditcard || 0),
    transferencia: money(m?.wire || 0),
    total: money(total),
    numpagos: String(m?.num_payments || 0),
    esperado: money(c.expected_amount || 0),
    contado: money(c.close_amount || 0),
    diferencia: difLinea,
    cobros: cobros.join('\n'),
    arqueo: arqueo.join('\n'),
    movimientos,
    conteovitrina,
    nota: c.notes ? `\n\n📝 *Nota:* ${c.notes}` : '',
  };
};

export const buildCorteText = (c: CorteData, negocio = "Let's Move Dance & Fitness"): string =>
  renderCajaTemplate(CAJA_CORTE_TEMPLATE_DEFAULT, buildCorteVars(c, negocio));

export const buildAperturaText = (
  s: { label: string; fondo: number; usuario?: string },
  negocio = "Let's Move Dance & Fitness",
): string => {
  const { fecha, hora } = fechaHora(new Date());
  return renderCajaTemplate(CAJA_APERTURA_TEMPLATE_DEFAULT, {
    negocio, fecha, hora, turno: s.label, fondo: money(s.fondo), usuario: s.usuario || '—',
  });
};
